import { Cron } from 'croner';

// One reading of a schedule for the server's tick and the panel (§7.2): same parser, same text.

export const MIN_SCHEDULE_INTERVAL_SEC = 60;
const GAP_SAMPLES = 24;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const MONTH_SHORT = MONTH_NAMES.map((name) => name.slice(0, 3).toUpperCase());
const NICKNAMES: Record<string, string> = {
  '@yearly': '0 0 1 1 *',
  '@annually': '0 0 1 1 *',
  '@monthly': '0 0 1 * *',
  '@weekly': '0 0 * * 0',
  '@daily': '0 0 * * *',
  '@midnight': '0 0 * * *',
  '@hourly': '0 * * * *',
};

export interface IScheduleCheck {
  ok: boolean;
  field?: 'cron' | 'timezone';
  message?: string;
}

export interface IScheduleRun {
  at: string;
  /** "Thu 24 Sep 09:00" in the schedule's own time zone. */
  local: string;
}

export interface IScheduleDescription {
  ok: boolean;
  message?: string;
  /** "At 09:00, Monday to Friday". */
  text: string;
  /** "Asia/Tbilisi · UTC+4". */
  timezoneLabel: string;
  next: IScheduleRun[];
}

function cronOf(pattern: string, timezone: string): Cron {
  return new Cron(expand(pattern), { timezone, paused: true, legacyMode: true });
}

function expand(pattern: string): string {
  const trimmed = pattern.trim().replace(/\s+/g, ' ');
  return NICKNAMES[trimmed.toLowerCase()] ?? trimmed;
}

export function isTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/** Five fields, a real zone, and never more often than once a minute. */
export function checkSchedule(pattern: string, timezone = 'UTC'): IScheduleCheck {
  if (!isTimezone(timezone)) {
    return { ok: false, field: 'timezone', message: `"${timezone}" is not a time zone` };
  }
  const fields = expand(pattern).split(' ');
  if (fields.length !== 5) {
    return {
      ok: false,
      field: 'cron',
      message: 'use five fields: minute, hour, day of month, month, day of week',
    };
  }
  let cron: Cron;
  try {
    cron = cronOf(pattern, timezone);
  } catch (error) {
    return { ok: false, field: 'cron', message: cleanCronError(error) };
  }
  const runs = cron.nextRuns(GAP_SAMPLES, new Date());
  if (runs.length === 0) return { ok: false, field: 'cron', message: 'this never runs' };
  for (let i = 1; i < runs.length; i += 1) {
    if (runs[i].getTime() - runs[i - 1].getTime() < MIN_SCHEDULE_INTERVAL_SEC * 1000) {
      return { ok: false, field: 'cron', message: 'a schedule runs at most once a minute' };
    }
  }
  return { ok: true };
}

/** The first run strictly after `after`, or null when the pattern never fires again. */
export function nextScheduleRun(pattern: string, timezone: string, after: Date): Date | null {
  return cronOf(pattern, timezone).nextRun(after);
}

// Slot to slot with `nextRun`, as the tick walks: preview equals firing across DST (§7.2).
export function nextScheduleRuns(
  pattern: string,
  timezone: string,
  after: Date,
  count: number,
): Date[] {
  const cron = cronOf(pattern, timezone);
  const runs: Date[] = [];
  let cursor: Date | null = after;
  while (runs.length < count) {
    cursor = cron.nextRun(cursor);
    if (!cursor) break;
    runs.push(cursor);
  }
  return runs;
}

/** Every slot in (`after`, `until`], capped: how many a downtime missed (§7.2 catch-up). */
export function scheduleSlotsBetween(
  pattern: string,
  timezone: string,
  after: Date,
  until: Date,
  cap = 1000,
): Date[] {
  const cron = cronOf(pattern, timezone);
  const slots: Date[] = [];
  let cursor: Date | null = after;
  while (slots.length < cap) {
    cursor = cron.nextRun(cursor);
    if (!cursor || cursor.getTime() > until.getTime()) break;
    slots.push(cursor);
  }
  return slots;
}

export function describeSchedule(
  pattern: string,
  timezone = 'UTC',
  options: { from?: Date; count?: number; first?: Date } = {},
): IScheduleDescription {
  const check = checkSchedule(pattern, timezone);
  const zone = isTimezone(timezone) ? timezone : 'UTC';
  const from = options.first ?? options.from ?? new Date();
  const timezoneLabel = `${zone} · ${utcOffsetLabel(zone, from)}`;
  if (!check.ok) {
    return { ok: false, message: check.message, text: pattern.trim(), timezoneLabel, next: [] };
  }
  const count = options.count ?? 4;
  const runs = options.first
    ? [options.first, ...nextScheduleRuns(pattern, zone, options.first, count - 1)]
    : nextScheduleRuns(pattern, zone, from, count);
  const next = runs.map((at) => ({
    at: at.toISOString(),
    local: formatLocal(at, zone),
  }));
  return { ok: true, text: describeCron(pattern), timezoneLabel, next };
}

/** "UTC+4", "UTC-3:30", "UTC" at the given instant (offsets move with daylight saving). */
export function utcOffsetLabel(timezone: string, at: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
  }).formatToParts(at);
  const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT';
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(name);
  if (!match) return 'UTC';
  const hours = String(Number(match[2]));
  return `UTC${match[1]}${hours}${match[3] === '00' ? '' : `:${match[3]}`}`;
}

function formatLocal(at: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')} ${get('hour')}:${get('minute')}`;
}

function cleanCronError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.replace(/^CronPattern:\s*/, '').slice(0, 200);
}

interface IField {
  any: boolean;
  step: number | null;
  values: number[] | null;
  range: [number, number] | null;
}

function parseField(raw: string, names: string[], offset = 0): IField | null {
  if (raw === '*' || raw === '?') return { any: true, step: null, values: null, range: null };
  const step = /^\*\/(\d+)$/.exec(raw);
  if (step) return { any: false, step: Number(step[1]), values: null, range: null };
  const number = (token: string): number | null => {
    const named = names.indexOf(token.toUpperCase());
    if (named >= 0) return named + offset;
    return /^\d+$/.test(token) ? Number(token) : null;
  };
  const range = /^([A-Za-z0-9]+)-([A-Za-z0-9]+)$/.exec(raw);
  if (range) {
    const [from, to] = [number(range[1]), number(range[2])];
    return from === null || to === null
      ? null
      : { any: false, step: null, values: null, range: [from, to] };
  }
  const values = raw.split(',').map(number);
  if (values.some((value) => value === null)) return null;
  return { any: false, step: null, values: values as number[], range: null };
}

const pad = (value: number) => String(value).padStart(2, '0');

function list(words: string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** English, the way the panel says it: "At 09:00, Monday to Friday". */
export function describeCron(pattern: string): string {
  const expanded = expand(pattern);
  const fields = expanded.split(' ');
  if (fields.length !== 5) return `Custom schedule: ${pattern.trim()}`;
  const minute = parseField(fields[0], []);
  const hour = parseField(fields[1], []);
  const dom = parseField(fields[2], []);
  const month = parseField(fields[3], MONTH_SHORT, 1);
  const dow = parseField(fields[4], DAY_SHORT);
  if (!minute || !hour || !dom || !month || !dow) return `Custom schedule: ${pattern.trim()}`;
  const time = describeTime(minute, hour);
  if (time === null) return `Custom schedule: ${pattern.trim()}`;
  const days = describeDays(dom, month, dow);
  if (days === null) return `Custom schedule: ${pattern.trim()}`;
  if (!days) return time.everyDay ? `${time.text}, every day` : time.text;
  return `${time.text}, ${days}`;
}

function describeTime(minute: IField, hour: IField): { text: string; everyDay: boolean } | null {
  const singleMinute = minute.values?.length === 1 ? minute.values[0] : null;
  const everyMinutes = (step: number | null) =>
    !step || step === 1 ? 'Every minute' : `Every ${step} minutes`;
  if (minute.any && hour.any) return { text: 'Every minute', everyDay: false };
  if (minute.step && hour.any) return { text: everyMinutes(minute.step), everyDay: false };
  if (singleMinute !== null) {
    if (hour.values) {
      if (hour.values.length > 6) return null;
      const times = hour.values.map((h) => `${pad(h)}:${pad(singleMinute)}`);
      return { text: `At ${list(times)}`, everyDay: true };
    }
    if (hour.any) return { text: `Every hour at :${pad(singleMinute)}`, everyDay: false };
    if (hour.step) {
      const every = hour.step === 1 ? 'Every hour' : `Every ${hour.step} hours`;
      return { text: `${every} at :${pad(singleMinute)}`, everyDay: false };
    }
    if (hour.range) {
      const [from, to] = hour.range;
      return {
        text: `Every hour at :${pad(singleMinute)} from ${pad(from)}:${pad(singleMinute)} to ${pad(to)}:${pad(singleMinute)}`,
        everyDay: false,
      };
    }
  }
  if (minute.values && hour.any) {
    return {
      text: `Every hour at ${list(minute.values.map((m) => `:${pad(m)}`))}`,
      everyDay: false,
    };
  }
  if ((minute.any || minute.step) && (hour.range || hour.values?.length === 1)) {
    const [from, to] = hour.range ?? [hour.values![0], hour.values![0]];
    return {
      text: `${everyMinutes(minute.step)} from ${pad(from)}:00 to ${pad(to)}:59`,
      everyDay: false,
    };
  }
  return null;
}

function describeDays(dom: IField, month: IField, dow: IField): string | null {
  if (dom.step || month.step || dow.step) return null;
  const weekdays = weekdayText(dow);
  const monthText = month.any
    ? null
    : month.range
      ? `${MONTH_NAMES[month.range[0] - 1]} to ${MONTH_NAMES[month.range[1] - 1]}`
      : list((month.values ?? []).map((m) => MONTH_NAMES[m - 1] ?? String(m)));
  let dayText: string | null = null;
  if (!dom.any) {
    const days = dom.range
      ? `days ${dom.range[0]} to ${dom.range[1]}`
      : dom.values!.length === 1
        ? `day ${dom.values![0]}`
        : `days ${list(dom.values!.map(String))}`;
    dayText = `on ${days} of ${monthText ?? 'the month'}`;
  }
  // Classic cron: a day of month and a weekday together mean either of them.
  const parts: string[] = [];
  if (dayText) parts.push(dayText);
  if (weekdays) parts.push(dayText ? `or ${weekdays}` : weekdays);
  if (!dayText && monthText) parts.push(month.range ? `from ${monthText}` : `in ${monthText}`);
  return parts.join(' ');
}

function weekdayText(dow: IField): string | null {
  if (dow.any) return null;
  const name = (day: number) => DAY_NAMES[day % 7];
  if (dow.range) {
    const [from, to] = dow.range;
    if (from === 1 && to === 5) return 'Monday to Friday';
    return `${name(from)} to ${name(to)}`;
  }
  const days = [...new Set(dow.values!.map((day) => day % 7))].sort((a, b) => a - b);
  if (days.length === 2 && days[0] === 0 && days[1] === 6) return 'on Saturday and Sunday';
  return `on ${list(days.map(name))}`;
}
